// DatePickerCalendar.tsx
import dayjs, { Dayjs } from "dayjs"
import { useMemo, useRef, useState } from "react"
import { classNames } from "utils/ClassNames"
import weekday from "dayjs/plugin/weekday"
import weekOfYear from "dayjs/plugin/weekOfYear"
import localeData from "dayjs/plugin/localeData"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { CalendarIcon } from "@heroicons/react/24/outline"
interface DatePickerCalendarProps {
  displayDate: Dayjs
  selectedDate: Dayjs | null
  onDateSelected: (date: Dayjs) => void
  onMonthChange: (event: React.ChangeEvent<HTMLSelectElement>) => void
  onYearChange: (event: React.ChangeEvent<HTMLSelectElement>) => void
  onlyFutureDates?: boolean
}

export const DatePickerCalendar: React.FC<DatePickerCalendarProps> = ({
  displayDate,
  selectedDate,
  onDateSelected,
  onMonthChange,
  onYearChange,
  onlyFutureDates
}) => {
  const weekDays = ["M", "T", "W", "T", "F", "S", "S"]
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ]

  const years = useMemo(() => {
    const currentYear = dayjs().year()
    const yearRange = 50
    return Array.from(
      { length: yearRange * 2 + 1 },
      (_, i) => currentYear - yearRange + i
    )
  }, [])

  const generateDays = useMemo(() => {
    const startOfMonth = displayDate.startOf("month")
    const endOfMonth = displayDate.endOf("month")

    let startDate = startOfMonth.startOf("week")
    if (startDate.day() === 0) {
      startDate = startDate.subtract(6, "day")
    } else {
      startDate = startDate.subtract(1, "day")
    }

    const endDate = endOfMonth.endOf("week").subtract(1, "day")
    const days: Dayjs[] = []
    let day = startDate

    while (day.isBefore(endDate) || day.isSame(endDate, "day")) {
      days.push(day)
      day = day.add(1, "day")
    }

    return days
  }, [displayDate])

  return (
    <div className="w-full min-w-[320px] bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 border dark:border-gray-700">
      <div className="flex items-center justify-between mb-4 gap-2">
        <select
          value={displayDate.month()}
          onChange={onMonthChange}
          className="p-2 border rounded-md flex-1"
        >
          {months.map((month, index) => (
            <option key={month} value={index}>
              {month}
            </option>
          ))}
        </select>

        <select
          value={displayDate.year()}
          onChange={onYearChange}
          className="p-2 border rounded-md w-28"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day, index) => (
          <div
            key={day + index}
            className="text-center text-gray-500 dark:text-gray-400 text-sm py-2"
          >
            {day}
          </div>
        ))}

        {generateDays.map((date, index) => {
          const isSelected = selectedDate?.isSame(date, "day")
          const isToday = date.isSame(dayjs(), "day")
          const isBeforeOrToday = isToday || date.isBefore(dayjs(), "day")
          const isCurrentMonth = date.month() === displayDate.month()

          return (
            <button
              key={index}
              onClick={() => onDateSelected(date)}
              type="button"
              disabled={isBeforeOrToday && onlyFutureDates}
              className={classNames(
                "p-2 text-center rounded-lg transition-colors",
                isSelected
                  ? "bg-primary-500 text-white"
                  : "hover:bg-gray-100 dark:hover:bg-gray-800",
                !isCurrentMonth
                  ? "text-gray-400 dark:text-gray-500"
                  : "text-gray-900 dark:text-white",
                isToday && !isSelected && !onlyFutureDates
                  ? "text-blue-600 font-bold"
                  : "",
                isBeforeOrToday && onlyFutureDates
                  ? "text-gray-400 dark:text-gray-500 opacity-30 hover:bg-white dark:hover:bg-gray-900"
                  : ""
              )}
            >
              {date.format("D")}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Set up dayjs plugins
dayjs.extend(weekday)
dayjs.extend(weekOfYear)
dayjs.extend(localeData)
dayjs.Ls.en.weekStart = 0

interface DatePickerProps {
  defaultDate?: Date
  onDateSelected?: (date: Date) => void
  onlyFutureDates?: boolean
  placeholder?: string
}

export const DatePicker: React.FC<DatePickerProps> = ({
  defaultDate,
  onDateSelected,
  onlyFutureDates,
  placeholder = "Select date"
}) => {
  const initialDate = useRef(defaultDate ? dayjs(defaultDate) : null)
  const [displayDate, setDisplayDate] = useState<Dayjs>(initialDate.current || dayjs())
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(initialDate.current)

  const handleDateSelected = (date: Dayjs) => {
    setSelectedDate(date)
    setDisplayDate(date)
    onDateSelected?.(date.toDate())
  }

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = parseInt(event.target.value)
    setDisplayDate(displayDate.month(newMonth))
  }

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = parseInt(event.target.value)
    setDisplayDate(displayDate.year(newYear))
  }

  return (
    <Popover className="relative">
      <PopoverButton className="w-full">
        <div className="flex items-center w-full ring-1 ring-inset ring-gray-300 dark:ring-gray-600 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600">
          <CalendarIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 ml-2" />
          <span
            className={classNames(
              "flex-1 text-left p-1.5 text-sm/6",
              selectedDate
                ? "text-gray-900 dark:text-white"
                : "text-gray-400 dark:text-gray-500"
            )}
          >
            {selectedDate ? selectedDate.format("MMMM D, YYYY") : placeholder}
          </span>
        </div>
      </PopoverButton>

      <PopoverPanel className="absolute z-10 mt-1">
        <DatePickerCalendar
          displayDate={displayDate}
          selectedDate={selectedDate}
          onDateSelected={handleDateSelected}
          onMonthChange={handleMonthChange}
          onYearChange={handleYearChange}
          onlyFutureDates={onlyFutureDates}
        />
      </PopoverPanel>
    </Popover>
  )
}
